/**
 * Field-level visibility policy.
 *
 * One place that answers two questions about a single field name:
 *
 *   1. "Which RBAC action does a user need before this field may be shown or
 *      returned?" — the RBAC-gated field families (gross profit, net cost).
 *   2. "May this field ever be delivered generically at all?" — the hard
 *      deny-list, for columns that are storage detail rather than display
 *      data and must never be reachable through a configured list column.
 *
 * Both are consumed in three places so the offer and the delivery can't
 * drift apart:
 *
 *   • `ListFieldHydrationService` (crm-api) — refuses to SELECT a field the
 *     caller may not see, so restricted values never leave the database.
 *   • `ObjectsService.getObjectFields` (crm-api) — omits them from the field
 *     metadata that feeds the list configurator, so the picker cannot show
 *     a field the user isn't allowed to have.
 *   • The list configurator (web-ui) — filters the same way client-side, so
 *     the menu is correct without waiting on a round trip.
 *
 * The API-side checks are the enforcement; the frontend check is presentation.
 * A spoofed request still gets nothing, because the hydrator applies the same
 * policy and `GpStripInterceptor` sanitizes the response afterwards.
 */
import { GP_VIEW_PERMISSION, isGpFieldName } from './gross-profit';
import { isItemCostFieldName, ITEM_COST_VIEW_PERMISSION } from './item-cost';
import { isNetCostFieldName, NET_COST_VIEW_PERMISSION } from './net-cost';

/**
 * An RBAC-gated family of field names: every field the detector matches
 * requires the paired permission.
 */
export interface FieldPermissionRule {
	/** Human-readable family name, used in tests and documentation. */
	readonly family: string;
	/** Returns true when a field name belongs to this family. */
	readonly matches: (fieldName: string) => boolean;
	/** RBAC action required to see fields in this family. */
	readonly permission: string;
}

/**
 * Every RBAC-gated field family, in one list.
 *
 * When a new family is introduced, add its detector here and all three
 * consumers above pick it up — there is no second place to update.
 */
export const FIELD_PERMISSION_RULES: ReadonlyArray<FieldPermissionRule> = [
	{
		family: 'gross-profit',
		matches: isGpFieldName,
		permission: GP_VIEW_PERMISSION,
	},
	{
		family: 'net-cost',
		matches: isNetCostFieldName,
		permission: NET_COST_VIEW_PERMISSION,
	},
	// Listed AFTER net-cost so `averageNetCost` resolves to the stricter
	// items:net_cost:view rather than this family's action.
	{
		family: 'item-cost',
		matches: isItemCostFieldName,
		permission: ITEM_COST_VIEW_PERMISSION,
	},
];

/**
 * Field names that are never delivered through a generically configured
 * column, for any user, regardless of permissions.
 *
 * These are stored values with no legitimate list-display use — payment
 * instrument details and anything that reads as a credential. A list column
 * is a user-authored config, so this is the boundary that keeps "configure a
 * column" from becoming "read an arbitrary column".
 *
 * Deliberately matched on the whole field name, case-insensitively, and
 * biased toward over-matching: a false positive hides a field from the
 * picker (annoying), a false negative exposes stored secrets (security
 * issue). Explicit business fields that would otherwise trip the pattern
 * are re-admitted by ALWAYS_ALLOWED_FIELD_NAMES below.
 */
const RESTRICTED_FIELD_PATTERNS: ReadonlyArray<RegExp> = [
	// Credentials and anything key-shaped.
	/pass(?:word|code|phrase)/i,
	/secret/i,
	/token/i,
	/credential/i,
	/api_?key/i,
	/private_?key/i,
	/\bsalt\b/i,
	/(?:^|[^a-z])hash(?:$|[^a-z])/i,
	// Payment instrument details. `lastFour` / card brand / expiry stay
	// visible — they are what payment-method lists legitimately display.
	/routing_?number/i,
	/account_?number/i,
	/(?:^|[^a-z])bin(?:$|[A-Z_0-9])/,
	/cvv|cvc|securitycode|security_code/i,
	/card_?number|cardnumber/i,
	// Government identifiers.
	/\bssn\b|social_?security/i,
];

/**
 * Field names that must stay available even though they match a restricted
 * pattern above. Compared case-insensitively against the whole name.
 *
 * `m_payment_externalId_paymentProcessor` is the gateway's own id for a
 * saved method — it is already shown on payment surfaces and carries no
 * instrument data. Everything else here is ordinary business vocabulary
 * that happens to contain a restricted word.
 */
const ALWAYS_ALLOWED_FIELD_NAMES: ReadonlySet<string> = new Set(
	['m_payment_externalId_paymentProcessor', 'tokenizedAt', 'binNumber'].map(
		(name) => name.toLowerCase(),
	),
);

/**
 * True when a field may never be delivered through a configured list column,
 * for anyone. Checked before the permission rules — no RBAC action unlocks
 * these.
 */
export function isRestrictedFieldName(fieldName: string): boolean {
	if (fieldName == null || fieldName === '') {
		return false;
	}

	if (ALWAYS_ALLOWED_FIELD_NAMES.has(fieldName.toLowerCase())) {
		return false;
	}

	// Custom fields are tenant-authored labels rather than schema columns;
	// evaluate the key itself, not the `cf:` prefix.
	const name = fieldName.startsWith('cf:') ? fieldName.slice(3) : fieldName;

	return RESTRICTED_FIELD_PATTERNS.some((pattern) => pattern.test(name));
}

/**
 * The RBAC action required to see a field, or null when the field belongs to
 * no gated family.
 */
export function requiredPermissionForField(fieldName: string): string | null {
	if (fieldName == null || fieldName === '') {
		return null;
	}

	const rule = FIELD_PERMISSION_RULES.find((r) => r.matches(fieldName));

	return rule?.permission ?? null;
}

/**
 * The single visibility predicate.
 *
 * @param fieldName - Bare field name (no table prefix).
 * @param can - Permission check for the current user. Backend callers pass
 *   `PermissionService.hasPermission`; the frontend passes its own
 *   `PermissionService.can`.
 */
export function canViewField(
	fieldName: string,
	can: (permission: string) => boolean,
): boolean {
	if (isRestrictedFieldName(fieldName)) {
		return false;
	}

	const permission = requiredPermissionForField(fieldName);

	return permission == null || can(permission);
}
